import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { REACTION_POW } from '@/lib/stashstr';
import { verifyPow } from '@/lib/pow';

export interface ReactionsData {
  upCount: number;
  downCount: number;
  /** Net score: upCount - downCount. Display-only. */
  score: number;
  myVote: 'up' | 'down' | undefined;
  myReactionId: string | undefined;
  /** Up-only count (kept so the Top "upvotes" metric keeps working). */
  count: number;
  hasUpvoted: boolean;
}

/**
 * Tallies upvotes (kind 7, content '+') and downvotes (content '-') for a
 * target event.
 *
 * Rules:
 * - Only proven reactions (REACTION_POW) count; unproven are excluded.
 * - Target-scoped: reaction `K/k` tag must equal `targetKind`.
 * - One vote per pubkey, latest wins: if a user switches up -> down, the
 *   up is dropped and the down counted (never both).
 * - One-way: no un-vote, no deletion handling.
 *
 * NOTE: relays index lowercase tags only; uppercase filters (#E) are
 * best-effort for NIP-22 compat. The lowercase #e filter carries the load.
 */
export function useReactions(
  targetId: string | undefined,
  myPubkey: string | undefined,
  targetKind: '11' | '1111',
) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['stashstr', 'reactions', targetId ?? '', myPubkey ?? '', targetKind],
    queryFn: async (c): Promise<ReactionsData> => {
      if (!targetId) throw new Error('No target id');

      // 1. Fetch reactions targeting the event (NIP-25 `E` + legacy `e`)
      const reactions = await nostr.query(
        [
          { kinds: [7], '#E': [targetId], limit: 200 },
          { kinds: [7], '#e': [targetId], limit: 200 },
        ],
        { signal: c.signal },
      );

      // Dedupe by id
      const byId = new Map<string, NostrEvent>();
      for (const r of reactions) {
        const targets = r.tags.some(([t, v]) => (t === 'E' || t === 'e') && v === targetId);
        if (targets && !byId.has(r.id)) byId.set(r.id, r);
      }

      // 2. Keep proven +/- reactions scoped to this target kind.
      // Legacy tagless reactions default to post scope; reject only
      // explicit cross-kind votes (K present and different).
      const proven = [...byId.values()].filter((r) => {
        if (r.content !== '+' && r.content !== '-') return false;
        if (!verifyPow(r, REACTION_POW)) return false;
        const k = r.tags.find(([t]) => t === 'K' || t === 'k')?.[1];
        return k === undefined || k === targetKind;
      });

      // 3. Latest proven reaction per pubkey wins (switching drops the old vote)
      const latestByPubkey = new Map<string, NostrEvent>();
      for (const r of proven) {
        const existing = latestByPubkey.get(r.pubkey);
        if (!existing || r.created_at > existing.created_at) {
          latestByPubkey.set(r.pubkey, r);
        }
      }

      // 4. Tally directions from the surviving votes
      let upCount = 0;
      let downCount = 0;
      for (const r of latestByPubkey.values()) {
        if (r.content === '+') upCount++;
        else downCount++;
      }

      const mine = myPubkey ? latestByPubkey.get(myPubkey) : undefined;
      const myVote = mine ? (mine.content === '+' ? 'up' : 'down') : undefined;

      return {
        upCount,
        downCount,
        score: upCount - downCount,
        myVote,
        myReactionId: mine?.id,
        count: upCount,
        hasUpvoted: myVote === 'up',
      };
    },
    staleTime: 15_000,
    enabled: Boolean(targetId),
  });
}
